/**
 * ProtectedRoute — gates dashboard pages.
 *
 * Reads teacher state from the TanStack `useTeacher()` query, NOT from
 * a Context Provider. The query's data lives in TanStack's module-level
 * cache, so this component works without any AuthProvider ancestor.
 *
 * Four states:
 *   - persisted-but-not-yet-confirmed → spinner (don't trust stale cache)
 *   - loading                          → spinner
 *   - unauthed                         → redirect to /login
 *   - authed but onboarding incomplete → redirect to /onboarding
 *   - authed + onboarded               → render children
 *
 * The persisted-but-not-yet-confirmed case is the subtle one. We persist
 * the teacher query to localStorage (so navigating around the app feels
 * instant on cold loads — no flash of "logged out" while the network
 * round-trips). But that means after, say, a fresh OAuth login + redirect
 * to /dashboard, the localStorage cache might still hold a stale `null`
 * from before the login; if we trusted it, we'd bounce the user right
 * back to /login. So when the cache value is falsy AND the query is
 * mid-fetch (or hasn't fetched at all yet this session), we wait. Once
 * the network responds — fresh teacher or confirmed-null — we route.
 *
 * This matters most on the cutover day for v3 prod (2026-04-26): users
 * had a stale TanStack persistence layer plus a bad JWT cookie from when
 * `app.teacherdashboard.ai` was pointing at the dev backend. Without
 * this fetchStatus check, they'd hit a redirect loop.
 */
import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTeacher } from "../api/queries/teacher";
import LoadingScreen from "./LoadingScreen";

export default function ProtectedRoute({ children }: { children: ReactNode }) {
  const { data: teacher, isLoading, isFetching, isFetched } = useTeacher();
  const location = useLocation();

  if (isLoading) {
    return <LoadingScreen />;
  }

  // If the persisted cache says "no teacher" but we haven't actually
  // confirmed that with the network yet (or are confirming right now),
  // wait for the fetch to settle before trusting the answer. Without
  // this, a fresh post-OAuth /dashboard navigation reads stale cache,
  // sees `teacher == null`, and redirects to /login before the
  // background refetch can return the real teacher.
  if (!teacher && (isFetching || !isFetched)) {
    return <LoadingScreen />;
  }

  if (!teacher) {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnTo=${returnTo}`} replace />;
  }

  if (!teacher.onboardingComplete) {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/onboarding?returnTo=${returnTo}`} replace />;
  }

  return <>{children}</>;
}
